import { useEffect, useMemo } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import ticketsClient, {
  TicketFacets,
  TicketSearchFilters,
} from "../../../api/tickets";
import { Ticket } from "../types";

export const TICKETS_QUERY_KEY = ["tickets"];
export const TICKET_FACETS_QUERY_KEY = ["ticket-facets"];
export const TICKET_DETAIL_QUERY_KEY = ["ticket-detail"];
export const ticketDetailQueryKey = (ticketId: string) => [
  ...TICKET_DETAIL_QUERY_KEY,
  ticketId,
];

const PAGE_SIZE = 100;
// Backstop so a very wide filter can never walk the whole collection in one go.
const MAX_PAGES = 20;

export type TicketSearchParams = {
  query?: string;
  filters?: TicketSearchFilters;
  sort?: Record<string, 1 | -1>;
};

export type TicketSearchResult = {
  tickets: Ticket[];
  total: number;
  loadedCount: number;
  isLoading: boolean;
  isFetching: boolean;
  hasMore: boolean;
  loadMore: () => void;
};

type TicketPage = { tickets: Ticket[]; total: number };

function countLoaded(pages: TicketPage[]): number {
  return pages.reduce((sum, page) => sum + page.tickets.length, 0);
}

/**
 * Runs one server-side search. The server owns every filter, the search text
 * and the paging; the client only decides how many pages to pull.
 *
 * `loadAll` keeps requesting pages until the result set is complete — views
 * that group, sort or drag-order tickets need the whole filtered set, not a
 * page of it. Views hitting MAX_PAGES stop and expose `loadMore`.
 */
export function useTicketSearch(
  params: TicketSearchParams,
  {
    loadAll = true,
    enabled = true,
  }: { loadAll?: boolean; enabled?: boolean } = {},
): TicketSearchResult {
  const query = useInfiniteQuery({
    queryKey: [...TICKETS_QUERY_KEY, params],
    enabled,
    queryFn: ({ pageParam }) =>
      ticketsClient.searchTickets({
        ...params,
        limit: PAGE_SIZE,
        skip: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (allPages.length >= MAX_PAGES) return undefined;
      const loaded = countLoaded(allPages);
      if (lastPage.tickets.length === 0 || loaded >= lastPage.total)
        return undefined;
      return loaded;
    },
    // Keep the previous result on screen while a new filter or keystroke loads,
    // so the board does not blank out on every change.
    placeholderData: keepPreviousData,
  });

  const { hasNextPage, isFetchingNextPage, isPlaceholderData, fetchNextPage } =
    query;

  useEffect(() => {
    if (!loadAll || !enabled) return;
    // Placeholder pages belong to the previous filter — paging on top of them
    // would append pages to a result set that is being replaced.
    if (isPlaceholderData) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [
    loadAll,
    enabled,
    isPlaceholderData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const tickets = useMemo(() => {
    const pages = query.data?.pages ?? [];
    const seenIds = new Set<string>();
    const merged: Ticket[] = [];
    for (const page of pages) {
      for (const ticket of page.tickets) {
        // Concurrent edits can shift a ticket between pages mid-load.
        if (seenIds.has(ticket.id)) continue;
        seenIds.add(ticket.id);
        merged.push(ticket);
      }
    }
    return merged;
  }, [query.data]);

  return {
    tickets,
    total: query.data?.pages[0]?.total ?? 0,
    loadedCount: tickets.length,
    isLoading: enabled && query.isPending,
    isFetching: query.isFetching,
    hasMore: !!hasNextPage,
    loadMore: fetchNextPage,
  };
}

const EMPTY_FACETS: TicketFacets = {
  labelStats: [],
  unlabeledCount: 0,
  assignees: [],
  reporters: [],
  total: 0,
};

/**
 * Option lists and label progress have to describe every ticket in scope, not
 * the filtered page on screen, so they come from their own aggregation.
 */
export function useTicketFacets(filters: TicketSearchFilters): TicketFacets {
  const { data } = useQuery({
    queryKey: [...TICKET_FACETS_QUERY_KEY, filters],
    queryFn: () => ticketsClient.getTicketFacets(filters),
    placeholderData: keepPreviousData,
  });
  return data ?? EMPTY_FACETS;
}

/**
 * Detail views can outlive the list page a ticket arrived on, so they read the
 * ticket by id and fall back to the copy they were handed.
 */
export function useTicketDetail(ticket: Ticket): Ticket {
  const { data } = useQuery({
    queryKey: ticketDetailQueryKey(ticket.id),
    queryFn: () => ticketsClient.getTicket(ticket.id),
    placeholderData: ticket,
  });
  return data ?? ticket;
}

/**
 * Reads a single ticket by id — used for the `?ticket=` deep link, which has to
 * open even when the ticket falls outside the filters currently applied.
 */
export function useTicketById(ticketId: string | null): Ticket | null {
  const { data } = useQuery({
    queryKey: ticketDetailQueryKey(ticketId ?? ""),
    queryFn: () => ticketsClient.getTicket(ticketId as string),
    enabled: !!ticketId,
  });
  return data ?? null;
}
